import Vue from 'vue'
import moment from 'moment-timezone'

export default new class VideonScheduler {
  encodeStartTime(startTime) {
    return encodeURIComponent(startTime)
  }

  async listDeviceEvents(deviceGuid, query = {}) {
    const response = await Vue.axios.get(
      '/devices/' + deviceGuid + '/scheduler/events',
      { params: query }
    )
    return response.data
  }

  async listFleetEvents(fleetGuid, query = {}) {
    const response = await Vue.axios.get(
      '/fleets/' + fleetGuid + '/scheduler/events',
      {
        params: query,
        paramsSerializer: {
          indexes: null,
        },
      }
    )
    return response.data
  }

  async getEvent(deviceGuid, startTime) {
    const response = await Vue.axios.get(
      '/devices/' +
        deviceGuid +
        '/scheduler/events/' +
        this.encodeStartTime(startTime)
    )
    return response.data
  }

  async createEvent(deviceGuid, payload) {
    const response = await Vue.axios.post(
      '/devices/' + deviceGuid + '/scheduler/events',
      payload
    )
    return response.data
  }

  async updateEvent(deviceGuid, startTime, payload, scope = 'this') {
    const response = await Vue.axios.patch(
      '/devices/' +
        deviceGuid +
        '/scheduler/events/' +
        this.encodeStartTime(startTime),
      payload,
      { params: { scope } }
    )
    return response.data
  }

  async deleteEvent(deviceGuid, startTime, scope = 'this') {
    const response = await Vue.axios.delete(
      '/devices/' +
        deviceGuid +
        '/scheduler/events/' +
        this.encodeStartTime(startTime),
      { params: { scope } }
    )
    return response.data
  }

  deviceDisplayLabelFromDevice(device) {
    if (!device) {
      return ''
    }
    if (
      typeof device.hasMetadataKey === 'function' &&
      device.hasMetadataKey('device_alias')
    ) {
      return Vue.helpers.htmlDecode(device.metadataValue('device_alias'))
    }
    if (device.serial_number) {
      return device.serial_number
    }
    return device.device_guid || ''
  }

  deviceDisplayLabel(deviceRow, metadata) {
    const aliasEntry = (metadata || []).find(
      (entry) => entry.key === 'device_alias' && entry.value
    )
    if (aliasEntry) {
      return Vue.helpers.htmlDecode(aliasEntry.value)
    }
    if (deviceRow && deviceRow.serial_number) {
      return deviceRow.serial_number
    }
    return (deviceRow && deviceRow.device_guid) || ''
  }

  async resolveDeviceDisplayName(deviceRow) {
    let metadata = []
    try {
      const response = await Vue.axios.get(
        '/devices/' + deviceRow.device_guid + '/metadata'
      )
      metadata = response.data.metadata || []
    } catch (err) {
      console.warn(
        'Scheduler metadata load failed for',
        deviceRow.device_guid,
        err
      )
    }
    return this.deviceDisplayLabel(deviceRow, metadata)
  }

  defaultEventWindow(timezone) {
    const tz = timezone || Vue.helpers.getDisplayTimezone()
    return {
      from: moment.tz(tz).subtract(30, 'days').startOf('day'),
      to: moment.tz(tz).add(365, 'days').endOf('day'),
    }
  }

  apiRangeFromMoments(fromMoment, toMoment) {
    return {
      from: fromMoment.clone().utc().format('YYYY-MM-DDTHH:mm:ss.SSS') + 'Z',
      to: toMoment.clone().utc().format('YYYY-MM-DDTHH:mm:ss.SSS') + 'Z',
    }
  }

  fleetEncoderDevices(fleet) {
    if (!fleet || !fleet.devices) {
      return []
    }
    return fleet.devices.filter((row) => this.isFleetEncoderDevice(row))
  }

  isFleetEncoderDevice(row) {
    if (!row || !row.device_guid) {
      return false
    }
    const device = Vue.devices.getDevice(row.device_guid, row)
    if (device && device.device_type === 'encoder') {
      return true
    }
    if (device && device.device_type && device.device_type !== 'encoder') {
      return false
    }
    const attrs = (device && device.device_attributes) || row
    const productName = attrs && attrs.product_name
    const decoderProducts = ['example_device', 'bd_sdi_decoder']
    if (productName && decoderProducts.includes(productName)) {
      return false
    }
    return !productName || ['', 'edgecaster', 'edgecaster_max'].includes(productName)
  }

  async loadDeviceEvents(deviceGuid, deviceName, options = {}) {
    const tz = options.timezone || Vue.helpers.getDisplayTimezone()
    const defaults = this.defaultEventWindow(tz)
    const from = options.from || defaults.from
    const to = options.to || defaults.to
    const range = this.apiRangeFromMoments(from, to)
    const data = await this.listDeviceEvents(deviceGuid, range)
    const name = deviceName || deviceGuid
    return (data.events || []).map((evt) => ({
      ...evt,
      device_guid: deviceGuid,
      device_name: name,
      scheduler_sync_pending: data.scheduler_sync_pending,
    }))
  }

  fleetDeviceDisplayName(device) {
    return this.deviceDisplayLabelFromDevice(device)
  }

  async loadFleetEvents(fleet, options = {}) {
    const devices = this.fleetEncoderDevices(fleet)
    if (!fleet || !fleet.fleet_guid || !devices.length) {
      return { events: [], failures: [] }
    }

    const tz = options.timezone || Vue.helpers.getDisplayTimezone()
    const defaults = this.defaultEventWindow(tz)
    const from = options.from || defaults.from
    const to = options.to || defaults.to
    const range = this.apiRangeFromMoments(from, to)
    const encoderGuids = new Set(devices.map((device) => device.device_guid))
    const labelByGuid = {}

    for (const device of devices) {
      const resolved = Vue.devices.getDevice(device.device_guid, device)
      labelByGuid[device.device_guid] = this.fleetDeviceDisplayName(
        resolved || device
      )
    }

    try {
      const data = await this.listFleetEvents(fleet.fleet_guid, range)
      const events = (data.events || [])
        .filter((evt) => encoderGuids.has(evt.device_guid))
        .map((evt) => ({
          ...evt,
          device_name: labelByGuid[evt.device_guid] || evt.device_guid,
        }))
      return { events, failures: [] }
    } catch (err) {
      console.warn(
        'Scheduler fleet events load failed for',
        fleet.fleet_guid,
        err
      )
      const message = Vue.helpers.parseError(err)
      return {
        events: [],
        failures: devices.map((device) => ({
          device_guid: device.device_guid,
          device_name: labelByGuid[device.device_guid],
          message,
        })),
      }
    }
  }

  eventKey(evt) {
    return evt.device_guid + '|' + evt.start_time
  }
}()
